import React, { useEffect, useState, useRef } from 'react';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend, Title, Filler, ScatterController } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import crosshairPlugin from 'chartjs-plugin-crosshair';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, Title, Filler, ScatterController, crosshairPlugin);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// X-axis bounds (vol_trend is normalized to [-1, 1])
const X_MIN = -1;
const X_MAX = 1;

// Bucket size for binned mean
const BUCKET_SIZE = 0.1;

const VolTrendChart = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hoverVal, setHoverVal] = useState(null);
    const chartRef = useRef(null);

    useEffect(() => {
        const controller = new AbortController();
        let isMounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${API_BASE_URL}/data/volatility`, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`Failed to fetch data: ${response.statusText}`);
                }

                const result = await response.json();
                if (!isMounted) return;
                setData(result.data || []);
            } catch (err) {
                if (err.name === 'AbortError') return;
                if (!isMounted) return;
                setError(err.message);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchData();
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, []);

    if (loading) return <p style={{ textAlign: 'center', padding: '30px' }}>Loading...</p>;
    if (error) return <p style={{ textAlign: 'center', padding: '30px', color: 'red' }}>Error: {error}</p>;
    if (!data || data.length === 0) return <p style={{ textAlign: 'center', padding: '30px' }}>No data available.</p>;

    // Filter to valid points with both vol_trend and forward_return_normalized
    const validPoints = data.filter(row =>
        row.vol_trend !== null && row.vol_trend !== undefined &&
        row.forward_return_normalized !== null && row.forward_return_normalized !== undefined
    );

    if (validPoints.length === 0) {
        return <p style={{ textAlign: 'center', padding: '30px' }}>Data loaded, but no trend data available yet.</p>;
    }

    const scatterPoints = validPoints
        .map(row => ({
            x: Number(row.vol_trend),
            y: Number(row.forward_return_normalized),
            date: row.date
        }))
        .filter(pt => pt.x >= X_MIN && pt.x <= X_MAX && !isNaN(pt.y));

    const pointColor = 'rgba(120, 120, 120, 0.4)';

    // Calculate rolling bucketed mean
    const bucketedMean = [];
    const HALF_WINDOW = BUCKET_SIZE / 2;
    const STEP_SIZE = 0.01;  // Step every 0.01 for smooth line

    for (let xPos = X_MIN; xPos <= X_MAX; xPos += STEP_SIZE) {
        const pointsInWindow = scatterPoints.filter(
            pt => pt.x >= xPos - HALF_WINDOW && pt.x < xPos + HALF_WINDOW
        );

        if (pointsInWindow.length >= 3) {
            const meanReturn = pointsInWindow.reduce((sum, pt) => sum + pt.y, 0) / pointsInWindow.length;
            bucketedMean.push({ x: Math.round(xPos * 1000) / 1000, y: meanReturn });
        }
    }

    // Fixed Y bounds
    const yMin = -2;
    const yMax = 2;

    // Current vol_trend value
    const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
    const latestWithTrend = sortedData.find(row => row.vol_trend !== null && row.vol_trend !== undefined);
    const currentTrend = latestWithTrend ? Number(latestWithTrend.vol_trend) : null;
    const latestDate = latestWithTrend?.date || 'Latest';

    const RETURN_THRESHOLD = 0.2;

    const verticalLineDataset = (currentTrend !== null && currentTrend >= X_MIN && currentTrend <= X_MAX)
        ? {
            type: 'line',
            label: `Current (${latestDate})`,
            data: [
                { x: currentTrend, y: yMin },
                { x: currentTrend, y: yMax }
            ],
            borderColor: 'rgba(220, 53, 69, 0.85)',
            borderWidth: 2,
            borderDash: [6, 6],
            pointRadius: 0
        }
        : null;

    const zeroLineDataset = {
        type: 'line',
        label: 'Zero Line',
        data: [{ x: X_MIN, y: 0 }, { x: X_MAX, y: 0 }],
        borderColor: 'rgba(0, 0, 0, 0.6)',
        borderWidth: 1.5,
        pointRadius: 0,
        order: 3
    };

    const createLineGradient = (ctx, chartArea) => {
        if (!chartArea) return 'rgba(65, 105, 225, 0.9)';
        const { top, bottom } = chartArea;
        const gradient = ctx.createLinearGradient(0, top, 0, bottom);
        const range = yMax - yMin;
        const thresholdPos = (yMax - RETURN_THRESHOLD) / range;
        gradient.addColorStop(0, 'rgba(65, 105, 225, 1)');
        gradient.addColorStop(thresholdPos - 0.01, 'rgba(65, 105, 225, 1)');
        gradient.addColorStop(thresholdPos + 0.01, 'rgba(220, 53, 69, 1)');
        gradient.addColorStop(1, 'rgba(220, 53, 69, 1)');
        return gradient;
    };

    const createFillGradient = (ctx, chartArea) => {
        if (!chartArea) return 'rgba(65, 105, 225, 0.25)';
        const { top, bottom } = chartArea;
        const gradient = ctx.createLinearGradient(0, top, 0, bottom);
        const range = yMax - yMin;
        const thresholdPos = (yMax - RETURN_THRESHOLD) / range;
        gradient.addColorStop(0, 'rgba(65, 105, 225, 0.35)');
        gradient.addColorStop(thresholdPos - 0.01, 'rgba(65, 105, 225, 0.3)');
        gradient.addColorStop(thresholdPos + 0.01, 'rgba(220, 53, 69, 0.25)');
        gradient.addColorStop(1, 'rgba(220, 53, 69, 0.35)');
        return gradient;
    };

    const chartData = {
        datasets: [
            {
                type: 'scatter',
                label: 'Data Points',
                data: scatterPoints,
                backgroundColor: pointColor,
                borderColor: pointColor,
                pointRadius: 1.5,
                pointHoverRadius: 3,
                order: 3
            },
            {
                type: 'line',
                label: 'Mean',
                data: bucketedMean,
                borderColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    return createLineGradient(ctx, chartArea);
                },
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    return createFillGradient(ctx, chartArea);
                },
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 0,
                tension: 0.3,
                stepped: false,
                fill: 'origin',
                order: 1
            },
            zeroLineDataset,
            ...(verticalLineDataset ? [verticalLineDataset] : [])
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    filter: (legendItem) => {
                        return legendItem.text === 'Mean' ||
                               legendItem.text.startsWith('Current');
                    }
                }
            },
            tooltip: { enabled: false },
            title: { display: false },
            crosshair: {
                line: {
                    color: 'rgba(0, 0, 0, 0.8)',
                    width: 1,
                    dashPattern: [5, 5]
                },
                sync: { enabled: false },
                zoom: { enabled: false },
                snap: { enabled: false }
            }
        },
        scales: {
            x: {
                type: 'linear',
                min: X_MIN,
                max: X_MAX,
                title: {
                    display: true,
                    text: '\u2190 Hedging Costs Accelerating | Hedging Costs Decelerating \u2192'
                },
                grid: { color: 'rgba(0,0,0,0.08)' },
                ticks: { stepSize: 0.2 }
            },
            y: {
                title: {
                    display: true,
                    text: '1-mo Forward Return (normalized)'
                },
                min: yMin,
                max: yMax,
                grid: { color: 'rgba(0,0,0,0.08)' },
                ticks: { stepSize: 1 }
            }
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ height: '500px', position: 'relative' }} onMouseLeave={() => setHoverVal(null)}>
                <div style={{
                    position: 'absolute',
                    top: '35px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 10,
                    pointerEvents: 'none',
                    opacity: hoverVal !== null ? 1 : 0,
                    transition: 'opacity 0.1s'
                }}>
                    Vol Trend: {hoverVal !== null ? hoverVal.toFixed(2) : '--'}
                </div>
                <Chart
                    ref={chartRef}
                    type="scatter"
                    data={chartData}
                    options={{
                        ...options,
                        onHover: (event, elements, chart) => {
                            if (!chart.chartArea) return;
                            const { left, right } = chart.chartArea;
                            const x = event.native?.offsetX;
                            if (x !== undefined && x >= left && x <= right) {
                                const xValue = chart.scales.x.getValueForPixel(x);
                                setHoverVal(xValue);
                            } else {
                                setHoverVal(null);
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default VolTrendChart;
